'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

const MOBILE_HEADER_QUERY = '(max-width: 700px)';
const SCROLL_DIRECTION_THRESHOLD = 8;

export default function HeaderNavigation({ children }: { children: ReactNode }) {
    const shellRef = useRef<HTMLDivElement>(null);
    const lastScrollY = useRef(0);
    const fixedState = useRef(false);
    const frame = useRef<number | null>(null);
    const [isFixed, setIsFixed] = useState(false);
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        const mobileQuery = window.matchMedia(MOBILE_HEADER_QUERY);

        const update = (resetDirection = false) => {
            const currentScrollY = Math.max(window.scrollY, 0);
            const shellTop = shellRef.current?.offsetTop ?? 0;
            const shouldFix = mobileQuery.matches && currentScrollY > shellTop;
            const isEnteringFixedState = shouldFix && !fixedState.current;
            const difference = currentScrollY - lastScrollY.current;

            setIsFixed(shouldFix);

            if (!shouldFix || resetDirection || isEnteringFixedState) {
                setIsVisible(true);
            } else if (difference > SCROLL_DIRECTION_THRESHOLD) {
                setIsVisible(false);
            } else if (difference < -SCROLL_DIRECTION_THRESHOLD) {
                setIsVisible(true);
            }

            fixedState.current = shouldFix;

            if (
                isEnteringFixedState ||
                Math.abs(difference) > SCROLL_DIRECTION_THRESHOLD ||
                resetDirection
            ) {
                lastScrollY.current = currentScrollY;
            }
        };

        const scheduleUpdate = () => {
            if (frame.current !== null) return;

            frame.current = window.requestAnimationFrame(() => {
                frame.current = null;
                update();
            });
        };

        const reset = () => {
            lastScrollY.current = Math.max(window.scrollY, 0);
            update(true);
        };

        reset();
        window.addEventListener('scroll', scheduleUpdate, { passive: true });
        window.addEventListener('resize', reset);
        mobileQuery.addEventListener('change', reset);

        return () => {
            if (frame.current !== null) window.cancelAnimationFrame(frame.current);
            window.removeEventListener('scroll', scheduleUpdate);
            window.removeEventListener('resize', reset);
            mobileQuery.removeEventListener('change', reset);
        };
    }, []);

    const stateClass = isVisible
        ? 'site-header__navigation--visible'
        : 'site-header__navigation--hidden';
    const stickyClasses = isFixed ? ` site-header__navigation--fixed ${stateClass}` : '';

    return (
        <div className='site-header__navigation-shell' ref={shellRef}>
            <div className={`site-header__navigation${stickyClasses}`}>{children}</div>
        </div>
    );
}
